// src/features/locker-assignments/components/LockerAssignmentList.tsx

import React, { useMemo } from 'react';
import {
  List,
  TextField,
  FunctionField,
  useTranslate,
  ListProps,
  usePermissions,
  ReferenceInput,
  useGetIdentity,
  useGetList,
  useListContext,
} from 'react-admin';
import {
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import CustomDataGrid from '../../../components/common/CustomDatagrid';
import LockerAssignmentActions from './LockerAssignmentActions';
import { AssignmentStatusChip } from './ui/AssignmentStatusChip';
import { LockerAssignmentListItem, AssignmentStatus } from '../types';
import CustomListToolbar from '@/components/common/CustomListToolbar';
import { CustomDateInputFilter, CustomSelectInputFilter, CustomTextInputFilter } from '@/components/common/CustomFilters';
import TagsPopover from './TagsPopover';
import AccessHistoryPopover from './AccessHistoryPopover';

// Componente interno que tiene acceso al contexto de la lista
const LockerAssignmentDataGrid: React.FC = () => {
  const translate = useTranslate();
  const { filterValues } = useListContext();

  // Obtener el hotelId actual del filtro
  const currentHotelId = filterValues?.hotelId;

  // Función para determinar si una asignación está caducada
  const isExpired = (record: LockerAssignmentListItem) => {
    if (record.status !== AssignmentStatus.Active) {
      return false;
    }
    const now = new Date();
    const expiresAt = new Date(record.expiresAt);
    return expiresAt < now;
  };

  // Estilo para filas caducadas usando rowSx
  const rowSx = (record: LockerAssignmentListItem) => {
    if (isExpired(record)) {
      return {
        backgroundColor: '#fff3cd', // Amarillo suave de advertencia
        borderLeft: '4px solid #ff9800', // Borde naranja para destacar
        '&:hover': {
          backgroundColor: '#ffe4a3 !important', // Mantener el color en hover
        },
      };
    }
    return {};
  };

  return (
    <CustomDataGrid
      rowClick="edit"
      bulkActionButtons={undefined}
      rowSx={rowSx}
    >
      {/* Actions column */}
      <FunctionField
        label=""
        render={(record: LockerAssignmentListItem) => (
          <LockerAssignmentActions record={record} hotelId={currentHotelId} />
        )}
        sortable={false}
      />
      
      {/* Reference */}
      <TextField
        source="displayReference"
        label={translate('resources.locker-assignments.fields.displayReference')}
      />
      
      {/* Status */}
      <FunctionField
        label={translate('resources.locker-assignments.fields.status')}
        render={(record: LockerAssignmentListItem) => (
          <AssignmentStatusChip status={record.status} />
        )}
      />
      
      {/* Requested Types */}
      <FunctionField
        label={translate('resources.locker-assignments.fields.requestedTypes')}
        render={(record: LockerAssignmentListItem) => {
          if (!record.requestedLockerTypes || record.requestedLockerTypes.length === 0) {
            return <Typography variant="body2">-</Typography>;
          }
          
          return (
            <Box>
              {record.requestedLockerTypes.map((type, index) => (
                <Typography key={index} variant="body2">
                  {type.requestedCount}x {type.typeName || translate('resources.locker-assignments.ui.no_name')}
                </Typography>
              ))}
            </Box>
          );
        }}
      />
      
      {/* Assigned Lockers */}
     <FunctionField
        label={translate('resources.locker-assignments.fields.assignedLockers')}
        render={(record: LockerAssignmentListItem) => {
        if (!record.assignedLockers) {
          return <Typography variant="body2">-</Typography>;
    }
    
    // Check if assignedLockers is a number or array
    if (typeof record.assignedLockers === 'number') {
      const count = record.assignedLockers;
      const translation = translate('resources.locker-assignments.ui.locker_count');
      
      if (!translation) {
        return (
          <Box>
            <Typography variant="body2">
              {count === 1 ? `${count} taquilla` : `${count} taquillas`}
            </Typography>
          </Box>
        );
      }
      
      const parts = translation.split('||||');
      const template = count === 1 ? parts[0] : (parts[1] || parts[0]);
      const text = template ? template.replace('%{count}', String(count)).trim() : `${count} taquillas`;
      
      return (
        <Box>
          <Typography variant="body2">{text}</Typography>
        </Box>
      );
    } else if (Array.isArray(record.assignedLockers)) {
      const lockersList = record.assignedLockers;
      
      if (lockersList.length === 0) {
        return <Typography variant="body2">-</Typography>;
      }
      
      const count = lockersList.length;
      const translation = translate('resources.locker-assignments.ui.locker_count');
      
      let text;
      if (!translation) {
        text = count === 1 ? `${count} taquilla` : `${count} taquillas`;
      } else {
        const parts = translation.split('||||');
        const template = count === 1 ? parts[0] : (parts[1] || parts[0]);
        text = template ? template.replace('%{count}', String(count)).trim() : `${count} taquillas`;
      }
      
      return (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {text}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {lockersList.map(l => l.displayNumber).join(', ')}
          </Typography>
        </Box>
      );
    }
    
    // Caso por defecto si no es ni número ni array
    return <Typography variant="body2">-</Typography>;
  }}
/>
          
      
      {/* Dates */}
      <FunctionField
        label={translate('resources.locker-assignments.fields.dates')}
        render={(record: LockerAssignmentListItem) => {
          const expired = isExpired(record);
          return (
            <Box>
              <Typography variant="body2">
                {new Date(record.startsAt).toLocaleDateString()} {new Date(record.startsAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </Typography>
              <Typography
                variant="caption"
                color={expired ? 'error' : 'text.secondary'}
                sx={{ fontWeight: expired ? 'bold' : 'normal' }}
              >
                {expired && '⚠️ '}
                {new Date(record.expiresAt).toLocaleDateString()} {new Date(record.expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </Typography>
            </Box>
          );
        }}
      />
      
      {/* Tags Column */}
      <FunctionField
        label={translate('resources.locker-assignments.fields.tags')}
        render={(record: LockerAssignmentListItem) => {
         
          const assignmentHotelId = record.hotelId || record.hotel?.id || currentHotelId;
          
          return (
            <TagsPopover
              assignmentId={String(record.id)}
              assignmentReference={record.displayReference}
              tagsCount={record.tagsCount || 0}
              hotelId={assignmentHotelId}
              assignmentStatus={record.status} // NUEVO: Pasar el status de la asignación
            />
          );
        }}
        sortable={false}
        textAlign="center"
      />
      
      {/* Usage */}
      <FunctionField
        label={translate('resources.locker-assignments.fields.usage')}
        render={(record: LockerAssignmentListItem) => {
          if (!record.usageSummary) {
            return (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2">-</Typography>
                <AccessHistoryPopover
                  assignmentId={String(record.id)}
                  accessCount={0}
                />
              </Box>
            );
          }
          
          const lastAccessDate = record.usageSummary.lastAccess 
            ? new Date(record.usageSummary.lastAccess)
            : null;
          
            // Reemplaza la función getRelativeTime con esta versión más segura:
          const getRelativeTime = (date: Date) => {
            const now = new Date();
            const diffMs = now.getTime() - date.getTime();
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            
            if (diffDays === 0) {
              const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
              if (diffHours === 0) {
                const diffMins = Math.floor(diffMs / (1000 * 60));
                const translation = translate('resources.locker-assignments.ui.minutes_ago');
                if (!translation) return `${diffMins} min ago`;
                
                const parts = translation.split('||||');
                const template = diffMins === 1 ? parts[0] : (parts[1] || parts[0]);
                return template ? template.replace('%{count}', String(diffMins)).trim() : `${diffMins} min ago`;
              }
              const translation = translate('resources.locker-assignments.ui.hours_ago');
              if (!translation) return `${diffHours} hours ago`;
              
              const parts = translation.split('||||');
              const template = diffHours === 1 ? parts[0] : (parts[1] || parts[0]);
              return template ? template.replace('%{count}', String(diffHours)).trim() : `${diffHours} hours ago`;
            } else if (diffDays === 1) {
              return translate('resources.locker-assignments.ui.yesterday') || 'Yesterday';
            } else {
              const translation = translate('resources.locker-assignments.ui.days_ago');
              if (!translation) return `${diffDays} days ago`;
              
              const parts = translation.split('||||');
              const template = diffDays === 1 ? parts[0] : (parts[1] || parts[0]);
              return template ? template.replace('%{count}', String(diffDays)).trim() : `${diffDays} days ago`;
            }
          };
                    
          return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {(() => {
                    const translation = translate('resources.locker-assignments.ui.access_count');
                    if (!translation) {
                      return record.usageSummary.accessCount === 1 
                        ? `${record.usageSummary.accessCount} acceso`
                        : `${record.usageSummary.accessCount} accesos`;
                    }
                    
                    const parts = translation.split('||||');
                    const template = record.usageSummary.accessCount === 1 ? parts[0] : (parts[1] || parts[0]);
                    return template 
                      ? template.replace('%{count}', String(record.usageSummary.accessCount)).trim()
                      : `${record.usageSummary.accessCount} accesos`;
                  })()}
                </Typography>
                {lastAccessDate && (
                  <Typography variant="caption" color="text.secondary">
                    {translate('resources.locker-assignments.ui.last_access')}: {getRelativeTime(lastAccessDate)}
                  </Typography>
                )}
              </Box>
              <AccessHistoryPopover
                assignmentId={String(record.id)}
                accessCount={record.usageSummary.accessCount}
                lastAccess={record.usageSummary.lastAccess}
              />
            </Box>
          );
        }}
      />
    </CustomDataGrid>
  );
};

const LockerAssignmentList: React.FC<ListProps> = (props) => {
  const translate = useTranslate();
  const { permissions } = usePermissions();
  const { data: identity, isLoading: identityLoading } = useGetIdentity();
  
  // Determinar roles y permisos
  const userRole = permissions || '';
  const isGroupAdmin = userRole === 'GroupAdmin';
  const isHotelAdmin = userRole === 'HotelAdmin';
  const isReceptionist = userRole === 'Receptionist';
  
  // Check permissions
  const canCreate = useMemo(() => {
    return ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'].includes(userRole);
  }, [userRole]);
  
  const canExport = true;
  
  // Determinar el filtro de hoteles según el rol
  const hotelFilter = useMemo(() => {
    if (isHotelAdmin || isReceptionist) {
      // Hotel fijo del usuario
      return identity?.hotelId ? { id: identity.hotelId } : {};
    } else if (isGroupAdmin) {
      // Solo hoteles del grupo
      return identity?.groupId ? { groupId: identity.groupId } : {};
    }
    // SuperAdmin ve todos
    return {};
  }, [isHotelAdmin, isReceptionist, isGroupAdmin, identity?.hotelId, identity?.groupId]);
  
  // Cargar hoteles disponibles según el rol
  const { data: hotels } = useGetList('hotels', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
    filter: hotelFilter,
  }, {
    enabled: !identityLoading,
  });
  
  // Determinar el valor por defecto del filtro de hotel
  const defaultHotelId = useMemo(() => {
    if (!hotels || hotels.length === 0) return undefined;
    
    if ((isHotelAdmin || isReceptionist) && identity?.hotelId) {
      // HotelAdmin/Receptionist: usar su hotel fijo
      return identity.hotelId;
    } else {
      // SuperAdmin/GroupAdmin: seleccionar el primer hotel
      return hotels[0].id;
    }
  }, [hotels, isHotelAdmin, isReceptionist, identity?.hotelId]);
  
  // Define filters - SIEMPRE incluir el selector de hotel
  const filters = useMemo(() => [
    // Búsqueda
    <CustomTextInputFilter
      key="search"
      source="search"
      label={translate('resources.locker-assignments.filters.search')}
      alwaysOn
    />,
    
    // SELECTOR DE HOTEL - Siempre visible al principio
    <ReferenceInput
      key="hotelId"
      source="hotelId"
      reference="hotels"
      label={translate('common.hotel')}
      alwaysOn
      sort={{ field: 'name', order: 'ASC' }}
      filter={hotelFilter}
      perPage={100}
    >
      <CustomSelectInputFilter 
        optionText="name"
        optionValue="id"
        disabled={isHotelAdmin || isReceptionist} // Deshabilitado para roles de hotel único
      />
    </ReferenceInput>,

    // Estado
    <CustomSelectInputFilter
      key="status"
      source="status"
      label={translate('resources.locker-assignments.fields.status')}
      alwaysOn
      choices={[
        { id: AssignmentStatus.Pending, name: translate('resources.locker-assignments.status.pending') },
        { id: AssignmentStatus.Active, name: translate('resources.locker-assignments.status.active') },
        { id: AssignmentStatus.Archived, name: translate('resources.locker-assignments.status.archived') },
      ]}
    />,
    
    // Fecha de inicio
    <CustomDateInputFilter
      key="startsAt"
      source="startsAt"
      label={translate('resources.locker-assignments.fields.startsAt')}
      alwaysOn
    />,
    
    // Fecha de expiración
    <CustomDateInputFilter
      key="expiresAt"
      source="expiresAt"
      label={translate('resources.locker-assignments.fields.expiresAt')}
      alwaysOn
    />,
  ], [translate, hotelFilter, isHotelAdmin, isReceptionist]);
  

  const filterDefaultValues = useMemo(() => {
    const defaults = {
      status: AssignmentStatus.Active, 
    };

    if (defaultHotelId) {
      return { ...defaults, hotelId: defaultHotelId };
    }

    return defaults;
  }, [defaultHotelId]);
  
  // Si todavía está cargando o no hay hotel por defecto
  if (identityLoading || !defaultHotelId) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Main list render
  return (
    <List
      {...props}
      title={translate('resources.locker-assignments.titles.list')}
      filters={filters}
      filterDefaultValues={filterDefaultValues}
      sort={{ field: 'createdAt', order: 'DESC' }}
      perPage={25}
      // Key para forzar re-render cuando cambia el hotel
      key={`locker-assignments-${defaultHotelId}`}
      actions={
        <CustomListToolbar 
          showCreate={canCreate}
          showExport={canExport}
          showFilterButton={false}
        />
      }
    >
      <LockerAssignmentDataGrid />
    </List>
  );
};

export default LockerAssignmentList;
